import { forwardRef, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  format,
  parse,
  startOfMonth,
  startOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isValid,
} from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFloating, offset, flip, shift, autoUpdate, FloatingPortal } from '@floating-ui/react';
import { cn } from '../utils/cn';
import { Label } from './Label';

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  clearable?: boolean;
  defaultOnClear?: string;
  showToday?: boolean;
  'data-testid'?: string;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      onBlur,
      name,
      label,
      error,
      hint,
      placeholder = 'Select date',
      disabled,
      className,
      id,
      clearable,
      defaultOnClear,
      showToday = true,
      'data-testid': testId,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => {
      if (value) {
        const parsed = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsed) ? parsed : new Date();
      }
      return new Date();
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const inputId = id || name || label?.toLowerCase().replace(/\s+/g, '-');

    const { refs, floatingStyles } = useFloating({
      open: isOpen,
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
      whileElementsMounted: autoUpdate,
    });

    const selectedDate = useMemo(() => {
      if (!value) return null;
      const parsed = parse(value, 'yyyy-MM-dd', new Date());
      return isValid(parsed) ? parsed : null;
    }, [value]);

    useEffect(() => {
      if (selectedDate) {
        setViewDate(selectedDate);
      }
    }, [selectedDate]);

    const calendarDays = useMemo(() => {
      // Always render a fixed 6-week (42-day) grid so the calendar height stays
      // constant across months. Depending on where its days fall, a month spans
      // 4–6 partial weeks; padding to a full 6 rows (with greyed adjacent-month
      // days, matching how leading days are already styled) keeps the popup —
      // and its prev/next-month buttons — from growing or shrinking as the user
      // pages through months.
      const calendarStart = startOfWeek(startOfMonth(viewDate));
      const TOTAL_CELLS = 6 * 7; // 6 weeks × 7 days
      return Array.from({ length: TOTAL_CELLS }, (_, index) => addDays(calendarStart, index));
    }, [viewDate]);

    const handleSelectDate = useCallback(
      (date: Date) => {
        const formatted = format(date, 'yyyy-MM-dd');
        onChange?.(formatted);
        setIsOpen(false);
      },
      [onChange],
    );

    const handlePrevMonth = useCallback(() => {
      setViewDate((prev) => subMonths(prev, 1));
    }, []);

    const handleNextMonth = useCallback(() => {
      setViewDate((prev) => addMonths(prev, 1));
    }, []);

    const handleClear = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        onChange?.(defaultOnClear || '');
        setIsOpen(false);
        if (defaultOnClear) {
          const parsed = parse(defaultOnClear, 'yyyy-MM-dd', new Date());
          if (isValid(parsed)) {
            setViewDate(parsed);
          }
        }
      },
      [onChange, defaultOnClear],
    );

    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const floating = refs.floating.current;
        const reference = refs.reference.current as HTMLElement | null;

        if (floating && !floating.contains(target) && reference && !reference.contains(target)) {
          setIsOpen(false);
          onBlur?.();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, refs.floating, refs.reference, onBlur]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        } else if (event.key === 'Enter' && !isOpen) {
          event.preventDefault();
          setIsOpen(true);
        }
      },
      [isOpen],
    );

    const displayValue = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';

    return (
      <div ref={containerRef} className="relative">
        {label && <Label htmlFor={inputId}>{label}</Label>}

        <input type="hidden" ref={ref} name={name} value={value || ''} />

        <button
          type="button"
          ref={refs.setReference}
          id={inputId}
          data-testid={testId}
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex items-center gap-2 rounded-lg border px-2 py-1 text-xs text-left',
            'bg-transparent text-text-primary',
            'dark:text-text-primary',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            error
              ? 'border-feedback-error focus:ring-feedback-error'
              : 'border-border-strong hover:border-border-input-hover dark:border-border-strong dark:hover:border-border-input-hover',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          )}
        >
          <Calendar className="h-4 w-4 text-text-muted shrink-0" />
          <span className={cn('flex-1 truncate', !displayValue && 'text-text-muted')}>
            {displayValue || placeholder}
          </span>
        </button>

        {error && <p className="text-xs text-feedback-error mt-1.5">{error}</p>}
        {hint && !error && <p className="text-xs text-text-muted mt-1.5">{hint}</p>}

        {isOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              data-testid={testId ? `${testId}-calendar` : 'calendar-popup'}
              className={cn(
                'z-50 p-3 rounded-xl shadow-lg border',
                'bg-white dark:bg-bg-elevated',
                'border-border-default',
              )}
            >
              {/* Month/Year header */}
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className={cn(
                    'p-1.5 rounded-lg transition-colors',
                    'hover:bg-bg-muted dark:hover:bg-bg-subtle',
                    'text-text-secondary dark:text-text-muted',
                  )}
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold text-text-primary dark:text-text-primary">
                  {format(viewDate, 'MMMM yyyy')}
                </span>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className={cn(
                    'p-1.5 rounded-lg transition-colors',
                    'hover:bg-bg-muted dark:hover:bg-bg-subtle',
                    'text-text-secondary dark:text-text-muted',
                  )}
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="h-8 w-8 flex items-center justify-center text-xs font-medium text-text-muted dark:text-text-muted"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid — always 6 rows (42 cells) for a stable height */}
              <div className="grid grid-cols-7 gap-1" data-testid={testId ? `${testId}-grid` : 'calendar-grid'}>
                {calendarDays.map((day, index) => {
                  const isCurrentMonth = isSameMonth(day, viewDate);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectDate(day)}
                      className={cn(
                        'h-8 w-8 rounded-lg text-sm transition-colors',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500',
                        isSelected
                          ? 'bg-action-primary text-white font-semibold'
                          : isToday
                            ? 'bg-bg-muted dark:bg-bg-subtle text-text-primary dark:text-text-primary font-medium'
                            : isCurrentMonth
                              ? 'text-text-primary dark:text-text-primary hover:bg-bg-muted dark:hover:bg-bg-subtle'
                              : 'text-text-muted hover:bg-bg-muted dark:hover:bg-bg-subtle',
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              {/* Footer actions */}
              {(showToday || (clearable && selectedDate)) && (
                <div className="mt-3 pt-3 border-t border-border-default flex gap-2">
                  {showToday && (
                    <button
                      type="button"
                      onClick={() => handleSelectDate(new Date())}
                      className={cn(
                        'flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors',
                        'text-action-primary hover:bg-action-primary/10',
                      )}
                    >
                      Today
                    </button>
                  )}
                  {clearable && selectedDate && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className={cn(
                        'flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors',
                        'text-text-muted hover:bg-bg-muted dark:hover:bg-bg-subtle',
                      )}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
          </FloatingPortal>
        )}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';
